import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from './AuthContext';
import { useTheme } from '@mui/material/styles';
import { CircularProgress, Container, Button, Box } from '@mui/material';
import TitleAndInfo from './TitleAndInfo';
import { exportToCsv } from '../utils/exportToCsv';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const MarketActives = () => {
    const { user } = useAuth();
    const [marketActives, setMarketActives] = useState([]);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const baseUrl = window.location.origin;

    const titleTop = "Most Active: "
    const title = "Discover High-Volume Stocks of the Day";
    const info = "The `Market Most Active` screener provides a list of stocks with the highest trading volume in a single day.";

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('/api/stocks/marketactives');

                if (response.data && response.data.data) {
                    setMarketActives(response.data.data);
                    setLoading(false);
                } else {
                    console.error('Invalid response data:', response.data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    // Columns configuration
    const columns = [
        {
            field: 'symbol',
            headerName: 'Company',
            minWidth: 400,
            renderCell: (params) => (
                <div>
                    <RouterLink
                        to={`${baseUrl}/asset/${params.row.symbol}`}
                        style={{ color: theme.palette.primary.main, textDecoration: 'none' }}
                    >
                        {params.row.symbol}
                    </RouterLink>
                    <div style={{ fontSize: '0.9em', color: '#888' }}>{params.row.name}</div>
                </div>
            ),
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'changesPercentage',
            headerName: '%Change',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <div style={{ color: params.value > 0 ? theme.palette.positive.main : params.value < 0 ? theme.palette.negative.main : theme.palette.lightGray.main }}>
                    {parseFloat(params.value).toFixed(2)}%
                </div>
            ),
            align: 'right',
            headerAlign: 'right',
        },
        {
            field: 'price',
            headerName: 'Price',
            flex: 1,
            minWidth: 150,
            align: 'right',
            headerAlign: 'right',
        },
        {
            field: 'change',
            headerName: 'Change',
            flex: 1,
            minWidth: 150,
            align: 'right',
            headerAlign: 'right',
        },
    ];

    const handleExport = () => {
        // Call the exportToCsv utility
        exportToCsv('market_actives.csv', columns, marketActives);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </div>
        )
    }

    return (
        <div className="table-container">
            <Container maxWidth="xl">

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexDirection={{ xs: 'column', sm: 'row' }} // Stacks vertically on xs, horizontally on sm and larger
                    mb={2}
                    gap={2} // Adds space between items when stacked
                >
                    <TitleAndInfo titleTop={titleTop} title={title} info={info} />

                    {user.plan.name === 'Premium' && (
                        <Button variant="contained" color="primary" startIcon={<FileDownloadIcon />} onClick={handleExport}>
                            Export
                        </Button>
                    )}
                </Box>

                <DataGrid
                    rows={marketActives}
                    columns={columns}
                    getRowId={(row) => row.symbol}
                    disableColumnMenu
                    pagination
                    paginationPosition="middle"
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    sx={{
                        '& .MuiDataGrid-cell': {
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontSize: '0.8rem',
                        },
                    }}
                />
            </Container>
        </div>
    );
};

export default MarketActives;